import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, ZoomIn, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Lightbox = ({ images, index, setIndex, onClose, alt }) => {
    const [zoomed, setZoomed] = useState(false);
    const [origin, setOrigin] = useState({ x: 50, y: 50 });

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") setIndex((index + 1) % images.length);
            if (e.key === "ArrowLeft") setIndex((index - 1 + images.length) % images.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [index, images.length, onClose, setIndex]);

    // Lock the page behind the overlay; locking <html> preserves the scroll offset.
    useEffect(() => {
        const root = document.documentElement;
        const prev = root.style.overflow;
        root.style.overflow = "hidden";
        return () => { root.style.overflow = prev; };
    }, []);

    const track = (e) => {
        if (!zoomed) return;
        const r = e.currentTarget.getBoundingClientRect();
        setOrigin({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-black flex flex-col"
            data-testid="gallery-lightbox"
        >
            <div className="flex items-center justify-between px-5 md:px-8 py-4 shrink-0">
                <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">
                    {alt} · {index + 1}/{images.length} · {zoomed ? "Click to zoom out" : "Click image to zoom in"}
                </div>
                <button
                    onClick={onClose}
                    data-testid="lightbox-close-button"
                    aria-label="Close zoom view"
                    className="gallery-ctl w-10 h-10 rounded-full flex items-center justify-center"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className="relative flex-1 overflow-hidden flex items-center justify-center px-4 pb-4">
                <div
                    className={`relative max-h-full max-w-[min(100%,900px)] overflow-hidden ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
                    onMouseMove={track}
                    onClick={() => setZoomed(!zoomed)}
                    data-testid="lightbox-zoom-area"
                >
                    <img
                        src={images[index]}
                        alt={`${alt} — zoomed view ${index + 1}`}
                        data-testid="lightbox-image"
                        draggable={false}
                        className="max-h-[78vh] w-auto object-contain select-none transition-transform duration-300 rounded-xl"
                        style={{
                            transform: zoomed ? "scale(2.4)" : "scale(1)",
                            transformOrigin: `${origin.x}% ${origin.y}%`,
                        }}
                    />
                </div>

                {images.length > 1 && (
                    <>
                        <button
                            onClick={() => { setZoomed(false); setIndex((index - 1 + images.length) % images.length); }}
                            data-testid="lightbox-prev-button"
                            aria-label="Previous image"
                            className="gallery-ctl absolute left-4 md:left-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                            onClick={() => { setZoomed(false); setIndex((index + 1) % images.length); }}
                            data-testid="lightbox-next-button"
                            aria-label="Next image"
                            className="gallery-ctl absolute right-4 md:right-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>
                    </>
                )}
            </div>
        </motion.div>
    );
};

export const ProductGallery = ({ images = [], alt = "" }) => {
    const [index, setIndex] = useState(0);
    const [dir, setDir] = useState(1);
    const [open, setOpen] = useState(false);
    const touchX = useRef(null);

    useEffect(() => setIndex(0), [images.length]);

    const go = (next) => {
        const total = images.length;
        if (!total) return;
        setDir(next > index ? 1 : -1);
        setIndex((next + total) % total);
    };

    if (!images.length) return null;

    return (
        <div className="w-full h-full flex flex-col" data-testid="product-gallery">
            <div
                className="relative flex-1 flex items-center justify-center overflow-hidden select-none"
                onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                    if (touchX.current === null) return;
                    const dx = e.changedTouches[0].clientX - touchX.current;
                    if (Math.abs(dx) > 40) go(dx < 0 ? index + 1 : index - 1);
                    touchX.current = null;
                }}
            >
                <AnimatePresence initial={false} mode="wait">
                    <motion.img
                        key={index}
                        src={images[index]}
                        alt={`${alt} — view ${index + 1}`}
                        data-testid={`gallery-image-${index}`}
                        onClick={() => setOpen(true)}
                        initial={{ opacity: 0, x: dir * 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: dir * -40 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="product-media__img max-h-full max-w-full object-contain cursor-zoom-in rounded-xl"
                        draggable={false}
                    />
                </AnimatePresence>

                <button
                    onClick={() => setOpen(true)}
                    data-testid="gallery-zoom-button"
                    aria-label="Zoom image"
                    className="gallery-ctl absolute top-3 right-3 md:top-4 md:right-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold"
                >
                    <ZoomIn className="w-3.5 h-3.5" /> Zoom
                </button>

                {images.length > 1 && (
                    <>
                        <button
                            onClick={() => go(index - 1)}
                            data-testid="gallery-prev-button"
                            aria-label="Previous image"
                            className="gallery-ctl absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => go(index + 1)}
                            data-testid="gallery-next-button"
                            aria-label="Next image"
                            className="gallery-ctl absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </>
                )}
            </div>

            {images.length > 1 && (
                <div className="flex items-center justify-center gap-3 px-4 py-4 overflow-x-auto no-scrollbar">
                    {images.map((src, i) => (
                        <button
                            key={src + i}
                            onClick={() => go(i)}
                            data-testid={`gallery-thumb-${i}`}
                            aria-label={`View image ${i + 1}`}
                            className={`shrink-0 w-14 h-16 md:w-16 md:h-20 rounded-sm border overflow-hidden transition-all ${
                                i === index ? "border-lime opacity-100" : "border-white/15 opacity-60 hover:opacity-100"
                            }`}
                        >
                            <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
                        </button>
                    ))}
                </div>
            )}

            {createPortal(
                <AnimatePresence>
                    {open && (
                        <Lightbox
                            images={images}
                            index={index}
                            setIndex={setIndex}
                            alt={alt}
                            onClose={() => setOpen(false)}
                        />
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

export default ProductGallery;
